// The theme choice on Profile (FR-022, FR-044; P-PROF-4): System, Light or Dark, as a radio group
// of 44 px targets. Selecting one applies immediately and is remembered on this device.
import { StyleSheet, View } from "react-native";

import type { ThemePreference } from "@/theme/theme";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

const OPTIONS: readonly { value: ThemePreference; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    group: { gap: t.space.sm, alignItems: "flex-start" },
    row: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    option: {
      paddingHorizontal: t.space.lg,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    selected: {
      borderColor: t.color.primary,
      backgroundColor: t.color.primarySoft,
    },
  }),
);

export function ThemeChoice() {
  const styles = useStyles();
  const { preference, setPreference } = useTheme();
  return (
    <View
      style={styles.group}
      role="radiogroup"
      accessibilityLabel="Theme"
      testID="theme-choice"
    >
      <Text weight="semiBold">Theme</Text>
      <View style={styles.row}>
        {OPTIONS.map(({ value, label }) => {
          const selected = preference === value;
          return (
            <PressableTarget
              key={value}
              testID={`theme-${value}`}
              accessibilityRole="radio"
              accessibilityLabel={label}
              accessibilityState={{ checked: selected, selected }}
              // React Native Web does not map accessibilityState.checked to aria-checked on a radio.
              aria-checked={selected}
              style={[styles.option, selected && styles.selected]}
              onPress={() => setPreference(value)}
            >
              <Text weight={selected ? "bold" : "medium"}>{label}</Text>
            </PressableTarget>
          );
        })}
      </View>
    </View>
  );
}
